import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  enrichStationWithThemeBackgrounds,
  readActiveStoredStation,
  resetHomeAppState,
  type StoredStationRecord,
} from "../../../utils/stationFlowStorage";
import { AppDispatch } from "../../../redux/store";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import StationLogo from "../../../assets/img/new-theme/station-logo.png";
import type { AiSelfieArt } from "../../../services/aiSelfieArtService";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import {
  resolveStationThemeBackground,
  resolveStationThemeLogo,
} from "../../../utils/stationTheme";

/** Time "Thank you!" stays on screen before returning to the home screen. */
const REDIRECT_DELAY_MS = 5000;

type ThankYouState = {
  stationData?: StoredStationRecord;
  selectedArt?: Partial<AiSelfieArt> | null;
};

function preloadImage(url: string): Promise<void> {
  const src = url.trim();
  if (!src) return Promise.resolve();

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
    if (img.complete) resolve();
  });
}

const ThankYou = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { stationData, selectedArt } = (location.state || {}) as ThankYouState;
  const [bgReady, setBgReady] = useState(false);

  const resolvedArt = useMemo(
    () =>
      (selectedArt as AiSelfieArt | null) ||
      readSelectedAiSelfieArtFromStorage(),
    [selectedArt]
  );

  const themeStation = useMemo(
    () =>
      enrichStationWithThemeBackgrounds(
        stationData || readActiveStoredStation(),
        resolvedArt
      ),
    [resolvedArt, stationData]
  );

  const backgroundUrl = useMemo(
    () => resolveStationThemeBackground(themeStation) || "",
    [themeStation]
  );

  const logoUrl = useMemo(
    () => resolveStationThemeLogo(resolvedArt) || StationLogo,
    [resolvedArt]
  );

  useEffect(() => {
    setBgReady(false);
    let cancelled = false;

    Promise.all([
      preloadImage(backgroundUrl),
      preloadImage(logoUrl),
    ]).then(() => {
      if (!cancelled) setBgReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [backgroundUrl, logoUrl]);

  useEffect(() => {
    const timer = setTimeout(() => {
      resetHomeAppState(dispatch);
      navigate("/", { replace: true });
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [dispatch, navigate]);

  return (
    <StationThemeLayout
      className={`successPaymentSection paymentSuccessThemePage thankYouThemePage${
        bgReady ? " is-bg-ready" : " is-bg-loading"
      }`}
      station={themeStation}
      showStationLogo={false}
      showBack={false}
      useThemeBackground={bgReady}
      backgroundOverride={bgReady ? backgroundUrl : null}
    >
      <div className="thankYouScrim" aria-hidden="true" />
      <div className="thankYouContent">
        <div className="thankYouBrandStack">
          <img
            src={logoUrl}
            alt="Station logo"
            className="thankYouLogo"
          />
          <div className="heading">Thank you!</div>
        </div>
      </div>
    </StationThemeLayout>
  );
};

export default ThankYou;
